import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import Login from './components/admin/Login';
import Register from './components/admin/Register';
import Employees from './components/employees/Employees';
import Home from './components/admin/Home';
import CustomFields from './components/employees/CustomFields';
import Attendance from './components/attendance/Attendance';
import AddAttendance from './components/attendance/AddAttendance';
import EditAttendance from './components/attendance/EditAttendance';
import AttStatus from './components/attendance/AttStatus';
import UserManagement from './components/UserManagement/UserManagement';
import CompanySettings from './components/settings/CompanySettings';
import SalarySettings from './components/salarysettings/SalarySettings';
import Shift from './components/others/Shift';
import Holiday from './components/others/Holiday';
import LeaveMaster from './components/others/leavemaster/LeaveMaster';
import LeaveType from './components/others/leavemaster/LeaveType';
import SalaryDetails from './components/SalaryDetails/SalaryDetails';
import SalaryClone from './components/SalaryDetails/SalaryClone';
import PayrollMain from './components/payroll/PayrollMain';
import PayRegister from './components/payroll/PayRegister';
import PaySlip from './components/payroll/PaySlip';
import Layoff from './components/attendance/Layoff';
import EmployeeExport from './components/employees/EmployeeExport';
import AttendanceView from './components/attendance/AttendanceView';
import PayrollApproval from './components/payroll/PayrollApproval';
import PayrollExport from './components/payroll/PayrollExport';
import StatutoryReport from './components/payroll/StatutoryReport';
import LeaveRequestsList from './components/others/leavemaster/LeaveRequestsList';
import PermissionRequests from './components/perrequest/PermissionRequests';
import OndutyRequests from './components/ondury/OndutyRequests';
import Policies from './components/others/Policies';
import Device from './components/others/Device';
import Download from './components/others/Download';
import AttendanceLogs from './components/others/AttendanceLogs';
import MobilePunchLogs from './components/others/MobilePunchLogs';
import FaceDescriptor from './components/others/FaceDescriptor';
import LeaveLedger from './components/others/leavemaster/LeaveLedger';
import { PayComponents, AddPayComponent, EditPayComponent } from './components/PayComponents';
// Import Salary Templates components
import {
  SalaryTemplates,
  AddSalaryTemplate,
  EditSalaryTemplate,
  AddTemplateComponent
} from './components/usertemplate';
import { AuthProvider, useAuth } from './context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';

const MODAL_ROUTE_PATTERNS = [
  /^\/attendance\/add$/,
  /^\/attendance\/edit\/[^/]+$/,
  /^\/pay-components\/add$/,
  /^\/pay-components\/edit\/[^/]+$/,
  /^\/salary-templates\/add$/,
  /^\/salary-templates\/edit\/[^/]+$/,
  /^\/salary-templates\/add-component\/[^/]+$/
];

const isModalRoutePath = (pathname) =>
  MODAL_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));

const AppHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const username = user?.username || user?.name || 'User';
  const cname =
    user?.company_name ||
    user?.compnay_name ||
    user?.company?.company_name ||
    user?.company?.name ||
    'Company';
  const handleMenuClick = () => {
    navigate('/menu');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="hrms-fixed-header">
      <div className="hrms-fixed-header__left">
        <Link to="/" className="hrms-header-logo-link" title="Go to Home" aria-label="Go to Home">
          <img src="https://hrms.vrishab.in/logo192.png" alt="VrisHr logo" className="hrms-header-logo" />
        </Link>
        <span className="hrms-company-pill" title={cname}>
          <i className="bi bi-building me-1" aria-hidden="true"></i>
          {cname}
        </span>
        <span className="hrms-username">Hello {username}</span>
      </div>
      <div className="hrms-fixed-header__right">
        <button type="button" className="btn btn-outline-light btn-sm" onClick={handleMenuClick}>
          <i className="bi bi-list me-1" aria-hidden="true"></i>
          Menu
        </button>
        <button type="button" className="btn btn-light btn-sm" onClick={handleLogout}>
          <i className="bi bi-box-arrow-right me-1" aria-hidden="true"></i>
          Logout
        </button>
      </div>
    </header>
  );
};

const AppFooter = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="hrms-fixed-footer">
      <span className="hrms-fixed-footer__brand">VRISHAB-HRMS</span>
      <span className="hrms-fixed-footer__copy">Copyright to Vrishab @ {currentYear}</span>
    </footer>
  );
};

const ProtectedLayout = ({ children }) => {
  const location = useLocation();
  const showHeader = !isModalRoutePath(location.pathname);

  if (!showHeader) {
    return children;
  }

  return (
    <>
      <AppHeader />
      <div className="hrms-page-content-with-header">
        {children}
      </div>
    </>
  );
};


// Protected Route Component with enhanced error handling
const ProtectedRoute = ({ children, requiredPermissions = [] }) => {
  const { isAuthenticated, user, userPermissions } = useAuth();
  const location = useLocation();

  // Debug logging
  console.log('ProtectedRoute:', { isAuthenticated, user, userPermissions });

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Safe permission check
  if (requiredPermissions && requiredPermissions.length > 0) {
    const safePermissions = Array.isArray(userPermissions) ? userPermissions : [];
    const hasPermission = requiredPermissions.some(permission =>
      safePermissions.includes(permission)
    );

    if (!hasPermission) {
      return (
        <div className="container mt-5">
          <div className="alert alert-danger text-center">
            <h4>Access Denied</h4>
            <p>You don't have permission to access this page.</p>
            <Navigate to="/" replace />
          </div>
        </div>
      );
    }
  }

  return <ProtectedLayout>{children}</ProtectedLayout>;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (isAuthenticated && (location.pathname === '/login' || location.pathname === '/register')) {
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  return children;
};

// Loading component
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center min-vh-100">
    <div className="text-center">
      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3 text-muted">Loading...</p>
    </div>
  </div>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container mt-5">
          <div className="alert alert-danger">
            <h4>Something went wrong</h4>
            <p>We're sorry, but something went wrong. Please try refreshing the page.</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
            <details style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
              {this.state.error && this.state.error.toString()}
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      <Route path="/statutory-report" element={
        <ProtectedRoute>
          <StatutoryReport />
        </ProtectedRoute>
      } />
      <Route path="/policies" element={
        <ProtectedRoute>
          <Policies />
        </ProtectedRoute>
      } />
      <Route path="/face-descriptor" element={
        <ProtectedRoute>
          <FaceDescriptor />
        </ProtectedRoute>
      } />
      <Route path="/leave-ledger" element={
        <ProtectedRoute>
          <LeaveLedger />
        </ProtectedRoute>
      } />
      <Route path="/device" element={
        <ProtectedRoute>
          <Device />
        </ProtectedRoute>
      } />
      <Route path="/download" element={
        <ProtectedRoute>
          <Download />
        </ProtectedRoute>
      } />
      <Route path="/permission-requests" element={
        <ProtectedRoute>
          <PermissionRequests />
        </ProtectedRoute>
      } />
      <Route path="/onduty-requests" element={
        <ProtectedRoute>
          <OndutyRequests />
        </ProtectedRoute>
      } />
      <Route path="/attendance-logs" element={
        <ProtectedRoute>
          <AttendanceLogs />
        </ProtectedRoute>
      } />
      <Route path="/mobile-punch-logs" element={
        <ProtectedRoute>
          <MobilePunchLogs />
        </ProtectedRoute>
      } />
      <Route path="/payroll-export" element={
        <ProtectedRoute>
          <PayrollExport />
        </ProtectedRoute>
      } />
      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      } />
      <Route path="/salary-clone" element={
        <ProtectedRoute>
          <SalaryClone />
        </ProtectedRoute>
      } />
      <Route path="/payroll-approval" element={
        <ProtectedRoute>
          <PayrollApproval />
        </ProtectedRoute>
      } />
      <Route path="/leave-request" element={
        <ProtectedRoute>
          <LeaveRequestsList />
        </ProtectedRoute>
      } />

      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      } />
      <Route path="/employees" element={
        <ProtectedRoute>
          <Employees />
        </ProtectedRoute>
      } />
      <Route path="/custom-fields" element={
        <ProtectedRoute>
          <CustomFields />
        </ProtectedRoute>
      } />
      <Route path="/attendance" element={
        <ProtectedRoute>
          <Attendance />
        </ProtectedRoute>
      } />
      <Route path="/attendance/add" element={
        <ProtectedRoute>
          <AddAttendance />
        </ProtectedRoute>
      } />
      <Route path="/attendance/edit/:id" element={
        <ProtectedRoute>
          <EditAttendance />
        </ProtectedRoute>
      } />
      <Route path="/att-status" element={
        <ProtectedRoute>
          <AttStatus />
        </ProtectedRoute>
      } />
      <Route path="/user-management" element={
        <ProtectedRoute>
          <UserManagement />
        </ProtectedRoute>
      } />
      <Route path="/company-settings" element={
        <ProtectedRoute>
          <CompanySettings />
        </ProtectedRoute>
      } />
      <Route path="/menu" element={
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      } />
      <Route path="/attendance-view" element={
        <ProtectedRoute>
          <AttendanceView />
        </ProtectedRoute>
      } />
      <Route path="/admin-panel" element={
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      } />

      {/* Pay Components Routes */}
      <Route path="/pay-components" element={
        <ProtectedRoute>
          <PayComponents />
        </ProtectedRoute>
      } />
      <Route path="/pay-components/add" element={
        <ProtectedRoute>
          <AddPayComponent />
        </ProtectedRoute>
      } />
      <Route path="/pay-components/edit/:id" element={
        <ProtectedRoute>
          <EditPayComponent />
        </ProtectedRoute>
      } />

      {/* Salary Templates Routes */}
      <Route path="/salary-templates" element={
        <ProtectedRoute>
          <SalaryTemplates />
        </ProtectedRoute>
      } />
      <Route path="/salary-details" element={
        <ProtectedRoute>
          <SalaryDetails />
        </ProtectedRoute>
      } />
      <Route path="/salary-templates/add" element={
        <ProtectedRoute>
          <AddSalaryTemplate />
        </ProtectedRoute>
      } />
      <Route path="/salary-templates/edit/:id" element={
        <ProtectedRoute>
          <EditSalaryTemplate />
        </ProtectedRoute>
      } />
      <Route path="/salary-templates/add-component/:templateId" element={
        <ProtectedRoute>
          <AddTemplateComponent />
        </ProtectedRoute>
      } />
      <Route path="/salary-settings" element={
        <ProtectedRoute>
          <SalarySettings />
        </ProtectedRoute>
      } />
      <Route path="/shifts" element={
        <ProtectedRoute>
          <Shift />
        </ProtectedRoute>
      } />
      <Route path="/payroll/register" element={
        <ProtectedRoute>
          <PayRegister />
        </ProtectedRoute>
      } />
      <Route path="/holidays" element={
        <ProtectedRoute>
          <Holiday />
        </ProtectedRoute>
      } />
      <Route path="/leave-master" element={
        <ProtectedRoute>
          <LeaveMaster />
        </ProtectedRoute>
      } />
      <Route path="/leave-type" element={
        <ProtectedRoute>
          <LeaveType />
        </ProtectedRoute>
      } />
      <Route path="/payroll" element={
        <ProtectedRoute>
          <PayrollMain />
        </ProtectedRoute>
      } />
      <Route path="/payroll/payslip" element={
        <ProtectedRoute>
          <PaySlip />
        </ProtectedRoute>
      } />
      <Route path="/layoff" element={
        <ProtectedRoute>
          <Layoff />
        </ProtectedRoute>
      } />
      <Route path="/employee-print" element={
        <ProtectedRoute>
          <EmployeeExport />
        </ProtectedRoute>
      } />
      {/* Fallback route */}
      <Route path="*" element={
        <div className="container mt-5">
          <div className="alert alert-warning text-center">
            <h4>Page Not Found</h4>
            <p>The page you're looking for doesn't exist.</p>
            <Navigate to="/" replace />
          </div>
        </div>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div className="App">
            <AppContent />
            <AppFooter />
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
